import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { getContainer } from '@framework/di/getContainer';
import {
  useTopCompanyCommonContext,
} from '@/creditflowresearch/application/TopCompanies/topCompaniesCommon/TopCompaniesCommonContext';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { prepareTopCompanyPayload } from '@/creditflowresearch/application/TopCompanies/helpers';
import { DEFAULT_PERIOD } from '@/creditflowresearch/application/TopCompanies/constants';
import CompanyRepository from '@/creditflowresearch/infrastructure/repository/CompanyRepository';
import useFetchCollection from '@/creditflowresearch/infrastructure/state/useFetchCollection';
import { ISSUER_NAME, PARENT_NAME } from '@/creditflowresearch/ui/components/TopCompanies/constants';
import { ITopCompany, ITopCompanyServerData } from '@/creditflowresearch/application/TopCompanies/interfaces';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import { checkAccess } from '@/creditflowresearch/application/User/access/checkAccess';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';
import { PATH_ONLY_GRANT_CONTEXT } from '@/creditflowresearch/application/User/access/grants/pagePatterns';

interface ICompanyServerData {
  serverData?: ITopCompanyServerData,
  companyRole: string,
}

const useTopCompany = ({
  serverData,
  companyRole,
}: ICompanyServerData) => {
  const container = getContainer();
  const { query } = useRouter();
  const { setIsExportAvailable, selectedPeriod, activeTab } = useTopCompanyCommonContext();
  const [showVolume, setShowVolume] = useState<boolean>(serverData?.showVolume || false);
  const [isFirstFetching, setIsFirstFetching] = useState<boolean>(!!serverData?.topCompanies.collection);
  const { pageFilters, checkAccessByUserRegions, checkAccessByUserProducts } = usePageFiltersContext();
  const { checkAccessByUserPlan } = useUserPlanContext();
  const isTabActive = companyRole === PARENT_NAME ? activeTab === ISSUER_NAME : activeTab === companyRole;
  // TODO: remove when the European market opening period ends
  const isOpenAccessPage = useAccessGrant(PATH_ONLY_GRANT_CONTEXT);
  // TODO: remove when the European market opening period ends
  const hasAccess = checkAccess([
    isOpenAccessPage || checkAccessByUserPlan(UserPlanType.PREMIUM),
    isOpenAccessPage || checkAccessByUserRegions(),
    isOpenAccessPage || checkAccessByUserProducts(),
  ]);

  const payload = prepareTopCompanyPayload({
    pageFilters,
    perPage: 9,
    dateFrom: selectedPeriod?.dateFrom,
    dateTo: selectedPeriod?.dateTo,
    period: selectedPeriod?.value || DEFAULT_PERIOD,
    includedCompanyRole: companyRole,
  });

  const companyRepository = container.get<CompanyRepository>(CompanyRepository);

  const fetchTopCompanies = useCallback(
      async (payload) => {
        const { search, sortBy, sortOrder, ...other } = payload;
        const response = await companyRepository.getTopCompanies(other);
        setIsExportAvailable(response.showExport);
        setShowVolume(response.showVolume);
        setIsFirstFetching(true);
        return response.topCompanies;
      },
      [],
  );

  useEffect(() => () => {
    topCompaniesListing.softReset();
  }, [pageFilters, selectedPeriod]);

  const topCompaniesListing = useFetchCollection<ITopCompany>(
      fetchTopCompanies,
      {
        enabled: !(isFirstFetching || !isTabActive || !hasAccess),
        payload,
        initialValues: {
          perPage: payload.perPage,
          totalCount: serverData?.topCompanies.totalCount,
          collection: serverData?.topCompanies.collection || [],
        },
      });

  useEffect(() => () => {
    setIsFirstFetching(false);
  }, [
    query,
    selectedPeriod,
    topCompaniesListing.page,
  ]);

  return {
    ...topCompaniesListing,
    showVolume,
  };
};

export default useTopCompany;
